import type { CashMovement, CashSession } from '@iobiz/shared'
import { api } from '../../api/client.js'
import { runCycle } from '../sync/engine.js'

/**
 * Ouvrir, clôturer et mouvementer la caisse — par le SERVEUR, en ligne.
 *
 * ⚠️ Ces trois gestes ne passent PAS par la file d'attente hors ligne, à la
 * différence des ventes. L'unicité de la caisse ouverte est garantie par un
 * index en base, et deux appareils qui ouvriraient chacun la leur pendant une
 * coupure produiraient deux sessions pour une seule journée — un rapprochement
 * impossible à démêler après coup, et le contraire de ce que la caisse existe
 * pour apporter.
 *
 * ⭐ Le compromis est tenable parce qu'il est ASYMÉTRIQUE : l'ouverture
 * demande le réseau, mais une fois la caisse ouverte, elle est répliquée sur
 * l'appareil et VENDRE reste possible sans réseau toute la journée. C'est
 * l'ouverture qu'on fait le matin, au calme ; c'est la vente qui ne peut pas
 * attendre.
 *
 * ⚠️ `runCycle` après chaque écriture : sans lui, l'écran continuerait
 * d'afficher la réplique d'avant — caisse fermée alors qu'on vient de
 * l'ouvrir — jusqu'au prochain cycle de synchronisation.
 */

export async function ouvrirCaisse(
  storeId: string,
  fond: number,
  notes?: string,
): Promise<CashSession> {
  const session = await api.post<CashSession>(
    `/stores/${storeId}/cash-sessions`,
    { openingFloat: fond, ...(notes ? { notes } : {}) },
    { sansAppareil: true },
  )
  await runCycle(storeId)
  return session
}

export async function cloturerCaisse(
  storeId: string,
  sessionId: string,
  compte: number,
  notes?: string,
): Promise<CashSession> {
  const session = await api.post<CashSession>(
    `/stores/${storeId}/cash-sessions/${sessionId}/close`,
    { closingCounted: compte, ...(notes ? { notes } : {}) },
    { sansAppareil: true },
  )
  await runCycle(storeId)
  return session
}

export async function ajouterMouvement(
  storeId: string,
  sessionId: string,
  /** Signé : positif pour un apport, négatif pour un prélèvement. */
  montant: number,
  motif: string,
  notes?: string,
): Promise<CashMovement> {
  const mouvement = await api.post<CashMovement>(
    `/stores/${storeId}/cash-sessions/${sessionId}/movements`,
    { amount: montant, reason: motif, ...(notes ? { notes } : {}) },
    { sansAppareil: true },
  )
  await runCycle(storeId)
  return mouvement
}
